/**
 * ToolForm Component (Task 6.5)
 *
 * Form for creating and editing tools.
 * Supports all configurable tool properties with validation.
 *
 * Features:
 * - Name: Text input (required)
 * - Body Part: Select dropdown with all body parts
 * - Min Degrees: Number input (-180 to 0)
 * - Max Degrees: Number input (0 to 180)
 * - Duration: Number input (0.1 to 10)
 * - Easing: Select dropdown (linear, ease-in, ease-out, ease-in-out)
 * - Color: Color picker or preset palette
 * - Icon: Icon selector (optional, for future use)
 * - HTTP Endpoints: Collapsible advanced section (optional)
 */

import { useState, useEffect } from 'react';
import { X, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import * as Select from '@radix-ui/react-select';
import * as Collapsible from '@radix-ui/react-collapsible';
import type {
  ToolDefinition,
  BodyPart,
  EasingFunction,
  RotationAxis,
} from '@/stores/toolStore';

/**
 * Available body parts for tool configuration
 * Note: 'control' is excluded - control tools are global, not puppet-specific
 */
type MovementBodyPart = Exclude<BodyPart, 'control'>;

const BODY_PARTS: { value: MovementBodyPart; label: string }[] = [
  { value: 'head', label: 'Head' },
  { value: 'body', label: 'Body' },
  { value: 'leftArm', label: 'Left Arm' },
  { value: 'rightArm', label: 'Right Arm' },
  { value: 'leftLeg', label: 'Left Leg' },
  { value: 'rightLeg', label: 'Right Leg' },
];

/**
 * Available easing functions
 */
const EASING_FUNCTIONS: { value: EasingFunction; label: string }[] = [
  { value: 'linear', label: 'Linear' },
  { value: 'ease-in', label: 'Ease In' },
  { value: 'ease-out', label: 'Ease Out' },
  { value: 'ease-in-out', label: 'Ease In Out' },
];

/**
 * Available rotation axes
 * X = front-back (chest to back)
 * Y = left-right
 * Z = top-bottom
 */
const ROTATION_AXES: {
  value: RotationAxis;
  label: string;
  description: string;
}[] = [
  { value: 'x', label: 'X', description: 'Front ↔ Back' },
  { value: 'y', label: 'Y', description: 'Left ↔ Right' },
  { value: 'z', label: 'Z', description: 'Top ↔ Bottom' },
];

/**
 * Preset colors for tool color selection
 */
const COLOR_PRESETS = [
  '#ef4444', // red
  '#f97316', // orange
  '#eab308', // yellow
  '#22c55e', // green
  '#14b8a6', // teal
  '#3b82f6', // blue
  '#8b5cf6', // purple
  '#ec4899', // pink
  '#6b7280', // gray
];

interface ToolFormData {
  name: string;
  bodyPart: BodyPart;
  axis: RotationAxis;
  minDegrees: number;
  maxDegrees: number;
  dockingPosition: number;
  startingPosition: number;
  defaultDuration: number;
  defaultEasing: EasingFunction;
  color: string;
  icon?: string;
  httpEndpoints?: {
    start?: string;
    stop?: string;
  };
}

interface ToolFormProps {
  /**
   * Tool data for editing (undefined for new tool)
   */
  tool?: ToolDefinition;

  /**
   * Callback when form is submitted
   */
  onSubmit: (data: ToolFormData) => void;

  /**
   * Callback when form is cancelled
   */
  onCancel: () => void;

  /**
   * Whether the form is in edit mode
   */
  isEditing?: boolean;
}

export function ToolForm({
  tool,
  onSubmit,
  onCancel,
  isEditing = false,
}: ToolFormProps) {
  // Form state
  const [name, setName] = useState(tool?.name || '');
  const [bodyPart, setBodyPart] = useState<BodyPart>(tool?.bodyPart || 'head');
  const [axis, setAxis] = useState<RotationAxis>(tool?.axis || 'x');
  const [minDegrees, setMinDegrees] = useState(tool?.minDegrees ?? 0);
  const [maxDegrees, setMaxDegrees] = useState(tool?.maxDegrees ?? 90);
  const [dockingPosition, setDockingPosition] = useState(
    tool?.dockingPosition ?? 0
  );
  const [startingPosition, setStartingPosition] = useState(
    tool?.startingPosition ?? 45
  );
  const [defaultDuration, setDefaultDuration] = useState(
    tool?.defaultDuration ?? 1.0
  );
  const [defaultEasing, setDefaultEasing] = useState<EasingFunction>(
    tool?.defaultEasing || 'ease-in-out'
  );
  const [color, setColor] = useState(tool?.color || '#3b82f6');
  const [httpStart, setHttpStart] = useState(tool?.httpEndpoints?.start || '');
  const [httpStop, setHttpStop] = useState(tool?.httpEndpoints?.stop || '');
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Validation state
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Reset form when tool changes (syncing form state from external tool prop)
  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (tool) {
      setName(tool.name);
      setBodyPart(tool.bodyPart);
      setAxis(tool.axis || 'x');
      setMinDegrees(tool.minDegrees);
      setMaxDegrees(tool.maxDegrees);
      setDockingPosition(tool.dockingPosition ?? 0);
      setStartingPosition(tool.startingPosition ?? 45);
      setDefaultDuration(tool.defaultDuration);
      setDefaultEasing(tool.defaultEasing);
      setColor(tool.color);
      setHttpStart(tool.httpEndpoints?.start || '');
      setHttpStop(tool.httpEndpoints?.stop || '');
    }
  }, [tool]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (minDegrees < 0 || minDegrees > 180) {
      newErrors.minDegrees = 'Min degrees must be between 0 and 180';
    }

    if (maxDegrees < 0 || maxDegrees > 180) {
      newErrors.maxDegrees = 'Max degrees must be between 0 and 180';
    }

    if (minDegrees >= maxDegrees) {
      newErrors.minDegrees = 'Min must be less than max';
    }

    if (dockingPosition < minDegrees || dockingPosition > maxDegrees) {
      newErrors.dockingPosition = `Must be between ${minDegrees} and ${maxDegrees}`;
    }

    if (startingPosition < minDegrees || startingPosition > maxDegrees) {
      newErrors.startingPosition = `Must be between ${minDegrees} and ${maxDegrees}`;
    }

    if (defaultDuration < 0.1 || defaultDuration > 10) {
      newErrors.defaultDuration = 'Duration must be between 0.1 and 10';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const formData: ToolFormData = {
      name: name.trim(),
      bodyPart,
      axis,
      minDegrees,
      maxDegrees,
      dockingPosition,
      startingPosition,
      defaultDuration,
      defaultEasing,
      color,
    };

    // Only include HTTP endpoints if they have values
    if (httpStart || httpStop) {
      formData.httpEndpoints = {};
      if (httpStart) formData.httpEndpoints.start = httpStart;
      if (httpStop) formData.httpEndpoints.stop = httpStop;
    }

    onSubmit(formData);
  };

  return (
    <motion.form
      onSubmit={handleSubmit}
      className="space-y-4"
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <h4
          className="text-sm font-medium"
          style={{ color: 'var(--color-dark-text)' }}
        >
          {isEditing ? 'Edit Tool' : 'New Tool'}
        </h4>
        <button
          type="button"
          onClick={onCancel}
          className="rounded p-1 transition-colors hover:bg-neutral-700"
          style={{ color: 'var(--color-dark-text-muted)' }}
          aria-label="Cancel"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Name field */}
      <div>
        <label
          htmlFor="tool-name"
          className="mb-1 block text-xs font-medium"
          style={{ color: 'var(--color-dark-text-muted)' }}
        >
          Tool Name *
        </label>
        <input
          id="tool-name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Enter tool name"
          className="w-full rounded-md border px-3 py-2 text-sm outline-none transition-colors focus:ring-2"
          style={{
            backgroundColor: 'var(--color-dark-bg)',
            borderColor: errors.name
              ? 'var(--color-body-control)'
              : 'var(--color-dark-border)',
            color: 'var(--color-dark-text)',
          }}
          aria-invalid={!!errors.name}
          aria-describedby={errors.name ? 'name-error' : undefined}
        />
        {errors.name && (
          <p
            id="name-error"
            className="mt-1 text-xs"
            style={{ color: 'var(--color-body-control)' }}
          >
            {errors.name}
          </p>
        )}
      </div>

      {/* Body Part field */}
      <div>
        <label
          htmlFor="tool-body-part"
          className="mb-1 block text-xs font-medium"
          style={{ color: 'var(--color-dark-text-muted)' }}
        >
          Body Part *
        </label>
        <Select.Root
          value={bodyPart}
          onValueChange={(v) => setBodyPart(v as BodyPart)}
        >
          <Select.Trigger
            id="tool-body-part"
            className="flex w-full items-center justify-between rounded-md border px-3 py-2 text-sm outline-none transition-colors focus:ring-2"
            style={{
              backgroundColor: 'var(--color-dark-bg)',
              borderColor: 'var(--color-dark-border)',
              color: 'var(--color-dark-text)',
            }}
            aria-label="Body Part"
          >
            <Select.Value />
            <Select.Icon>
              <ChevronDown
                className="h-4 w-4"
                style={{ color: 'var(--color-dark-text-muted)' }}
              />
            </Select.Icon>
          </Select.Trigger>

          <Select.Portal>
            <Select.Content
              className="overflow-hidden rounded-md border"
              style={{
                backgroundColor: 'var(--color-dark-panel)',
                borderColor: 'var(--color-dark-border)',
                zIndex: 'var(--z-index-modal)',
              }}
            >
              <Select.Viewport className="p-1">
                {BODY_PARTS.map((bp) => (
                  <Select.Item
                    key={bp.value}
                    value={bp.value}
                    className="cursor-pointer rounded px-3 py-2 text-sm outline-none transition-colors data-[highlighted]:bg-neutral-700"
                    style={{ color: 'var(--color-dark-text)' }}
                  >
                    <Select.ItemText>{bp.label}</Select.ItemText>
                  </Select.Item>
                ))}
              </Select.Viewport>
            </Select.Content>
          </Select.Portal>
        </Select.Root>
      </div>

      {/* Axis field */}
      <div>
        <label
          htmlFor="tool-axis"
          className="mb-1 block text-xs font-medium"
          style={{ color: 'var(--color-dark-text-muted)' }}
        >
          Rotation Axis *
        </label>
        <Select.Root
          value={axis}
          onValueChange={(v) => setAxis(v as RotationAxis)}
        >
          <Select.Trigger
            id="tool-axis"
            className="flex w-full items-center justify-between rounded-md border px-3 py-2 text-sm outline-none transition-colors focus:ring-2"
            style={{
              backgroundColor: 'var(--color-dark-bg)',
              borderColor: 'var(--color-dark-border)',
              color: 'var(--color-dark-text)',
            }}
            aria-label="Rotation Axis"
          >
            <Select.Value />
            <Select.Icon>
              <ChevronDown
                className="h-4 w-4"
                style={{ color: 'var(--color-dark-text-muted)' }}
              />
            </Select.Icon>
          </Select.Trigger>

          <Select.Portal>
            <Select.Content
              className="overflow-hidden rounded-md border"
              style={{
                backgroundColor: 'var(--color-dark-panel)',
                borderColor: 'var(--color-dark-border)',
                zIndex: 'var(--z-index-modal)',
              }}
            >
              <Select.Viewport className="p-1">
                {ROTATION_AXES.map((ax) => (
                  <Select.Item
                    key={ax.value}
                    value={ax.value}
                    className="cursor-pointer rounded px-3 py-2 text-sm outline-none transition-colors data-[highlighted]:bg-neutral-700"
                    style={{ color: 'var(--color-dark-text)' }}
                  >
                    <Select.ItemText>
                      {ax.label} ({ax.description})
                    </Select.ItemText>
                  </Select.Item>
                ))}
              </Select.Viewport>
            </Select.Content>
          </Select.Portal>
        </Select.Root>
      </div>

      {/* Min/Max Degrees fields - side by side */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label
            htmlFor="tool-min-degrees"
            className="mb-1 block text-xs font-medium"
            style={{ color: 'var(--color-dark-text-muted)' }}
          >
            Min Degrees
          </label>
          <input
            id="tool-min-degrees"
            type="number"
            value={minDegrees}
            onChange={(e) => setMinDegrees(Number(e.target.value))}
            min={-180}
            max={0}
            step={1}
            className="w-full rounded-md border px-3 py-2 text-sm outline-none transition-colors focus:ring-2"
            style={{
              backgroundColor: 'var(--color-dark-bg)',
              borderColor: errors.minDegrees
                ? 'var(--color-body-control)'
                : 'var(--color-dark-border)',
              color: 'var(--color-dark-text)',
            }}
            aria-invalid={!!errors.minDegrees}
            aria-describedby={
              errors.minDegrees ? 'min-degrees-error' : undefined
            }
          />
          {errors.minDegrees && (
            <p
              id="min-degrees-error"
              className="mt-1 text-xs"
              style={{ color: 'var(--color-body-control)' }}
            >
              {errors.minDegrees}
            </p>
          )}
        </div>

        <div>
          <label
            htmlFor="tool-max-degrees"
            className="mb-1 block text-xs font-medium"
            style={{ color: 'var(--color-dark-text-muted)' }}
          >
            Max Degrees
          </label>
          <input
            id="tool-max-degrees"
            type="number"
            value={maxDegrees}
            onChange={(e) => setMaxDegrees(Number(e.target.value))}
            min={0}
            max={180}
            step={1}
            className="w-full rounded-md border px-3 py-2 text-sm outline-none transition-colors focus:ring-2"
            style={{
              backgroundColor: 'var(--color-dark-bg)',
              borderColor: errors.maxDegrees
                ? 'var(--color-body-control)'
                : 'var(--color-dark-border)',
              color: 'var(--color-dark-text)',
            }}
            aria-invalid={!!errors.maxDegrees}
            aria-describedby={
              errors.maxDegrees ? 'max-degrees-error' : undefined
            }
          />
          {errors.maxDegrees && (
            <p
              id="max-degrees-error"
              className="mt-1 text-xs"
              style={{ color: 'var(--color-body-control)' }}
            >
              {errors.maxDegrees}
            </p>
          )}
        </div>
      </div>

      {/* Docking and Starting Position fields - side by side */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label
            htmlFor="tool-docking-position"
            className="mb-1 block text-xs font-medium"
            style={{ color: 'var(--color-dark-text-muted)' }}
          >
            Docking Position
          </label>
          <input
            id="tool-docking-position"
            type="number"
            value={dockingPosition}
            onChange={(e) => setDockingPosition(Number(e.target.value))}
            min={minDegrees}
            max={maxDegrees}
            step={1}
            className="w-full rounded-md border px-3 py-2 text-sm outline-none transition-colors focus:ring-2"
            style={{
              backgroundColor: 'var(--color-dark-bg)',
              borderColor: errors.dockingPosition
                ? 'var(--color-body-control)'
                : 'var(--color-dark-border)',
              color: 'var(--color-dark-text)',
            }}
            aria-invalid={!!errors.dockingPosition}
            aria-describedby={
              errors.dockingPosition ? 'docking-position-error' : undefined
            }
          />
          <p
            className="mt-1 text-[10px]"
            style={{ color: 'var(--color-dark-text-muted)' }}
          >
            Rest/transport position
          </p>
          {errors.dockingPosition && (
            <p
              id="docking-position-error"
              className="mt-1 text-xs"
              style={{ color: 'var(--color-body-control)' }}
            >
              {errors.dockingPosition}
            </p>
          )}
        </div>

        <div>
          <label
            htmlFor="tool-starting-position"
            className="mb-1 block text-xs font-medium"
            style={{ color: 'var(--color-dark-text-muted)' }}
          >
            Starting Position
          </label>
          <input
            id="tool-starting-position"
            type="number"
            value={startingPosition}
            onChange={(e) => setStartingPosition(Number(e.target.value))}
            min={minDegrees}
            max={maxDegrees}
            step={1}
            className="w-full rounded-md border px-3 py-2 text-sm outline-none transition-colors focus:ring-2"
            style={{
              backgroundColor: 'var(--color-dark-bg)',
              borderColor: errors.startingPosition
                ? 'var(--color-body-control)'
                : 'var(--color-dark-border)',
              color: 'var(--color-dark-text)',
            }}
            aria-invalid={!!errors.startingPosition}
            aria-describedby={
              errors.startingPosition ? 'starting-position-error' : undefined
            }
          />
          <p
            className="mt-1 text-[10px]"
            style={{ color: 'var(--color-dark-text-muted)' }}
          >
            Initial animation position
          </p>
          {errors.startingPosition && (
            <p
              id="starting-position-error"
              className="mt-1 text-xs"
              style={{ color: 'var(--color-body-control)' }}
            >
              {errors.startingPosition}
            </p>
          )}
        </div>
      </div>

      {/* Duration and Easing fields - side by side */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label
            htmlFor="tool-duration"
            className="mb-1 block text-xs font-medium"
            style={{ color: 'var(--color-dark-text-muted)' }}
          >
            Duration (seconds)
          </label>
          <input
            id="tool-duration"
            type="number"
            value={defaultDuration}
            onChange={(e) => setDefaultDuration(Number(e.target.value))}
            min={0.1}
            max={10}
            step={0.1}
            className="w-full rounded-md border px-3 py-2 text-sm outline-none transition-colors focus:ring-2"
            style={{
              backgroundColor: 'var(--color-dark-bg)',
              borderColor: errors.defaultDuration
                ? 'var(--color-body-control)'
                : 'var(--color-dark-border)',
              color: 'var(--color-dark-text)',
            }}
            aria-invalid={!!errors.defaultDuration}
            aria-describedby={
              errors.defaultDuration ? 'duration-error' : undefined
            }
          />
          {errors.defaultDuration && (
            <p
              id="duration-error"
              className="mt-1 text-xs"
              style={{ color: 'var(--color-body-control)' }}
            >
              {errors.defaultDuration}
            </p>
          )}
        </div>

        <div>
          <label
            htmlFor="tool-easing"
            className="mb-1 block text-xs font-medium"
            style={{ color: 'var(--color-dark-text-muted)' }}
          >
            Easing
          </label>
          <Select.Root
            value={defaultEasing}
            onValueChange={(v) => setDefaultEasing(v as EasingFunction)}
          >
            <Select.Trigger
              id="tool-easing"
              className="flex w-full items-center justify-between rounded-md border px-3 py-2 text-sm outline-none transition-colors focus:ring-2"
              style={{
                backgroundColor: 'var(--color-dark-bg)',
                borderColor: 'var(--color-dark-border)',
                color: 'var(--color-dark-text)',
              }}
              aria-label="Easing function"
            >
              <Select.Value />
              <Select.Icon>
                <ChevronDown
                  className="h-4 w-4"
                  style={{ color: 'var(--color-dark-text-muted)' }}
                />
              </Select.Icon>
            </Select.Trigger>

            <Select.Portal>
              <Select.Content
                className="overflow-hidden rounded-md border"
                style={{
                  backgroundColor: 'var(--color-dark-panel)',
                  borderColor: 'var(--color-dark-border)',
                  zIndex: 'var(--z-index-modal)',
                }}
              >
                <Select.Viewport className="p-1">
                  {EASING_FUNCTIONS.map((ef) => (
                    <Select.Item
                      key={ef.value}
                      value={ef.value}
                      className="cursor-pointer rounded px-3 py-2 text-sm outline-none transition-colors data-[highlighted]:bg-neutral-700"
                      style={{ color: 'var(--color-dark-text)' }}
                    >
                      <Select.ItemText>{ef.label}</Select.ItemText>
                    </Select.Item>
                  ))}
                </Select.Viewport>
              </Select.Content>
            </Select.Portal>
          </Select.Root>
        </div>
      </div>

      {/* Color field */}
      <div>
        <label
          className="mb-1 block text-xs font-medium"
          style={{ color: 'var(--color-dark-text-muted)' }}
        >
          Color
        </label>
        <div className="flex items-center gap-3">
          {/* Color presets */}
          <div className="flex flex-wrap gap-1.5">
            {COLOR_PRESETS.map((preset) => (
              <button
                key={preset}
                type="button"
                onClick={() => setColor(preset)}
                className="h-6 w-6 rounded-full border-2 transition-transform hover:scale-110"
                style={{
                  backgroundColor: preset,
                  borderColor: color === preset ? 'white' : 'transparent',
                }}
                aria-label={`Select color ${preset}`}
                title={preset}
              />
            ))}
          </div>

          {/* Custom color input */}
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              className="h-8 w-8 cursor-pointer rounded border-0 bg-transparent"
              aria-label="Custom color"
            />
            <input
              type="text"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              className="w-20 rounded-md border px-2 py-1 text-xs"
              style={{
                backgroundColor: 'var(--color-dark-bg)',
                borderColor: 'var(--color-dark-border)',
                color: 'var(--color-dark-text)',
              }}
              placeholder="#hex"
            />
          </div>
        </div>
      </div>

      {/* Advanced section (HTTP endpoints) */}
      <Collapsible.Root open={showAdvanced} onOpenChange={setShowAdvanced}>
        <Collapsible.Trigger asChild>
          <button
            type="button"
            className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-xs font-medium transition-colors hover:bg-neutral-700"
            style={{ color: 'var(--color-dark-text-muted)' }}
          >
            <ChevronDown
              className={`h-3 w-3 transition-transform ${
                showAdvanced ? 'rotate-0' : '-rotate-90'
              }`}
            />
            Advanced Settings
          </button>
        </Collapsible.Trigger>

        <Collapsible.Content>
          <AnimatePresence>
            {showAdvanced && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mt-2 space-y-3 overflow-hidden rounded-md border p-3"
                style={{
                  backgroundColor: 'var(--color-dark-bg)',
                  borderColor: 'var(--color-dark-border)',
                }}
              >
                <p
                  className="text-xs"
                  style={{ color: 'var(--color-dark-text-muted)' }}
                >
                  HTTP endpoints for hardware control (optional)
                </p>

                <div>
                  <label
                    htmlFor="tool-http-start"
                    className="mb-1 block text-xs font-medium"
                    style={{ color: 'var(--color-dark-text-muted)' }}
                  >
                    Start Endpoint
                  </label>
                  <input
                    id="tool-http-start"
                    type="url"
                    value={httpStart}
                    onChange={(e) => setHttpStart(e.target.value)}
                    placeholder="http://..."
                    className="w-full rounded-md border px-3 py-2 text-sm outline-none transition-colors focus:ring-2"
                    style={{
                      backgroundColor: 'var(--color-dark-panel)',
                      borderColor: 'var(--color-dark-border)',
                      color: 'var(--color-dark-text)',
                    }}
                  />
                </div>

                <div>
                  <label
                    htmlFor="tool-http-stop"
                    className="mb-1 block text-xs font-medium"
                    style={{ color: 'var(--color-dark-text-muted)' }}
                  >
                    Stop Endpoint
                  </label>
                  <input
                    id="tool-http-stop"
                    type="url"
                    value={httpStop}
                    onChange={(e) => setHttpStop(e.target.value)}
                    placeholder="http://..."
                    className="w-full rounded-md border px-3 py-2 text-sm outline-none transition-colors focus:ring-2"
                    style={{
                      backgroundColor: 'var(--color-dark-panel)',
                      borderColor: 'var(--color-dark-border)',
                      color: 'var(--color-dark-text)',
                    }}
                  />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </Collapsible.Content>
      </Collapsible.Root>

      {/* Action buttons */}
      <div className="flex justify-end gap-3 pt-2">
        <motion.button
          type="button"
          onClick={onCancel}
          className="rounded-md px-4 py-2 text-sm font-medium transition-colors"
          style={{
            backgroundColor: 'transparent',
            borderWidth: '1px',
            borderStyle: 'solid',
            borderColor: 'var(--color-dark-border)',
            color: 'var(--color-dark-text)',
          }}
          whileHover={{
            scale: 1.02,
            backgroundColor: 'var(--color-dark-bg)',
          }}
          whileTap={{ scale: 0.98 }}
        >
          Cancel
        </motion.button>

        <motion.button
          type="submit"
          className="rounded-md px-4 py-2 text-sm font-medium text-white transition-colors"
          style={{
            backgroundColor: 'var(--color-body-head)',
          }}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          {isEditing ? 'Save' : 'Create'}
        </motion.button>
      </div>
    </motion.form>
  );
}
